/**
 * Enhanced Product Gallery System
 * Features: Advanced Zoom, Lightbox, Transitions, Hotspots, Before/After, Video Controls, etc.
 * @version 1.0.0
 */

class EnhancedProductGallery {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      enableZoom: options.enableZoom !== false,
      enableLightbox: options.enableLightbox !== false,
      enableTransitions: options.enableTransitions !== false,
      enableKenBurns: options.enableKenBurns || false,
      enableHotspots: options.enableHotspots || false,
      enableKeyboard: options.enableKeyboard !== false,
      enablePreload: options.enablePreload !== false,
      enableCounter: options.enableCounter !== false,
      zoomLevel: options.zoomLevel || 3,
      transitionDuration: options.transitionDuration || 400,
      autoplayDelay: options.autoplayDelay || 0,
      ...options
    };

    this.currentIndex = 0;
    this.images = [];
    this.isLightboxOpen = false;
    this.zoomLevel = 1;
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.translateX = 0;
    this.translateY = 0;

    this.init();
  }

  init() {
    this.collectMedia();
    this.createLightbox();
    this.attachEventListeners();
    if (this.options.enableKeyboard) this.initKeyboardNav();
    if (this.options.enablePreload) this.preloadImages();
    if (this.options.enableCounter) this.createCounter();
    this.initLightboxClicks();
    this.initZoom();
  }

  collectMedia() {
    const mediaElements = this.container.querySelectorAll('.product-gallery__media');
    this.images = Array.from(mediaElements).map((el, index) => ({
      element: el,
      type: el.dataset.mediaType || 'image',
      id: el.dataset.mediaId,
      src: this.getMediaSrc(el),
      alt: this.getMediaAlt(el),
      index: index,
      hotspots: this.parseHotspots(el)
    }));
  }

  getMediaSrc(element) {
    const img = element.querySelector('img');
    const video = element.querySelector('video');
    if (img) return img.src.replace(/\_\d+x\d+/, '');  // Remove size suffix for full res
    if (video) return video.querySelector('source')?.src;
    return '';
  }

  getMediaAlt(element) {
    const img = element.querySelector('img');
    return img?.alt || '';
  }

  parseHotspots(element) {
    const hotspotData = element.dataset.hotspots;
    return hotspotData ? JSON.parse(hotspotData) : [];
  }

  // ============ LIGHTBOX ============
  createLightbox() {
    if (!this.options.enableLightbox) return;

    const lightbox = document.createElement('div');
    lightbox.className = 'enhanced-gallery-lightbox';
    lightbox.innerHTML = `
      <div class="lightbox-overlay"></div>
      <div class="lightbox-content">
        <button class="lightbox-close" aria-label="Close lightbox">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <button class="lightbox-prev" aria-label="Previous image">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <button class="lightbox-next" aria-label="Next image">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
        <div class="lightbox-image-container">
          <img class="lightbox-image" alt="" />
        </div>
        <div class="lightbox-counter"></div>
        <div class="lightbox-hotspots"></div>
        <div class="lightbox-zoom-controls">
          <button class="zoom-in" aria-label="Zoom in">+</button>
          <button class="zoom-out" aria-label="Zoom out">−</button>
          <button class="zoom-reset" aria-label="Reset zoom">⟲</button>
        </div>
      </div>
    `;
    document.body.appendChild(lightbox);
    this.lightbox = lightbox;

    // Lightbox event listeners
    lightbox.querySelector('.lightbox-close').addEventListener('click', () => this.closeLightbox());
    lightbox.querySelector('.lightbox-overlay').addEventListener('click', () => this.closeLightbox());
    lightbox.querySelector('.lightbox-prev').addEventListener('click', () => this.navigateLightbox(-1));
    lightbox.querySelector('.lightbox-next').addEventListener('click', () => this.navigateLightbox(1));
    lightbox.querySelector('.zoom-in').addEventListener('click', () => this.adjustZoom(0.5));
    lightbox.querySelector('.zoom-out').addEventListener('click', () => this.adjustZoom(-0.5));
    lightbox.querySelector('.zoom-reset').addEventListener('click', () => this.resetZoom());

    // Drag to pan
    const imageContainer = lightbox.querySelector('.lightbox-image-container');
    imageContainer.addEventListener('mousedown', (e) => this.startDrag(e));
    imageContainer.addEventListener('mousemove', (e) => this.drag(e));
    imageContainer.addEventListener('mouseup', () => this.endDrag());
    imageContainer.addEventListener('mouseleave', () => this.endDrag());

    // Touch support
    imageContainer.addEventListener('touchstart', (e) => this.startDrag(e.touches[0]));
    imageContainer.addEventListener('touchmove', (e) => this.drag(e.touches[0]));
    imageContainer.addEventListener('touchend', () => this.endDrag());

    // Pinch zoom
    imageContainer.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });
  }

  openLightbox(index = 0) {
    this.currentIndex = index;
    this.isLightboxOpen = true;
    this.lightbox.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.updateLightboxImage();
  }

  closeLightbox() {
    this.isLightboxOpen = false;
    this.lightbox.classList.remove('active');
    document.body.style.overflow = '';
    this.resetZoom();
  }

  navigateLightbox(direction) {
    this.currentIndex = (this.currentIndex + direction + this.images.length) % this.images.length;
    this.updateLightboxImage();
  }

  updateLightboxImage() {
    const current = this.images[this.currentIndex];
    const img = this.lightbox.querySelector('.lightbox-image');
    const counter = this.lightbox.querySelector('.lightbox-counter');

    img.src = current.src;
    img.alt = current.alt;

    if (this.options.enableCounter) {
      counter.textContent = `${this.currentIndex + 1} / ${this.images.length}`;
    }

    this.renderHotspots(current.hotspots);
    this.resetZoom();
  }

  // ============ LIGHTBOX CLICKS ============
  initLightboxClicks() {
    if (!this.options.enableLightbox) return;

    this.images.forEach((img, index) => {
      const element = img.element;
      element.style.cursor = 'pointer';
      element.addEventListener('click', (e) => {
        if (!this.isLightboxOpen) {
          this.openLightbox(index);
        }
      });
    });
  }

  // ============ ZOOM (HOVER MAGNIFIER) ============
  initZoom() {
    if (!this.options.enableZoom) return;

    this.images.forEach((img, index) => {
      const element = img.element;

      // Desktop hover zoom magnifier
      if (window.innerWidth > 768) {
        this.createMagnifier(element);
      }
    });
  }

  createMagnifier(element) {
    const magnifier = document.createElement('div');
    magnifier.className = 'image-magnifier';
    element.appendChild(magnifier);

    element.addEventListener('mouseenter', () => {
      magnifier.style.display = 'block';
    });

    element.addEventListener('mouseleave', () => {
      magnifier.style.display = 'none';
    });

    element.addEventListener('mousemove', (e) => {
      const rect = element.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const xPercent = (x / rect.width) * 100;
      const yPercent = (y / rect.height) * 100;

      magnifier.style.left = `${x}px`;
      magnifier.style.top = `${y}px`;
      magnifier.style.backgroundPosition = `${xPercent}% ${yPercent}%`;

      const img = element.querySelector('img');
      if (img) {
        magnifier.style.backgroundImage = `url(${img.src})`;
      }
    });
  }

  adjustZoom(delta) {
    this.zoomLevel = Math.max(1, Math.min(this.options.zoomLevel, this.zoomLevel + delta));
    this.applyZoom();
  }

  resetZoom() {
    this.zoomLevel = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.applyZoom();
  }

  applyZoom() {
    const img = this.lightbox.querySelector('.lightbox-image');
    img.style.transform = `scale(${this.zoomLevel}) translate(${this.translateX}px, ${this.translateY}px)`;
  }

  handleWheel(e) {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    this.adjustZoom(delta);
  }

  // ============ DRAG TO PAN ============
  startDrag(e) {
    if (this.zoomLevel <= 1) return;
    this.isDragging = true;
    this.startX = e.clientX - this.translateX;
    this.startY = e.clientY - this.translateY;
  }

  drag(e) {
    if (!this.isDragging) return;
    this.translateX = e.clientX - this.startX;
    this.translateY = e.clientY - this.startY;
    this.applyZoom();
  }

  endDrag() {
    this.isDragging = false;
  }

  // ============ HOTSPOTS ============
  renderHotspots(hotspots) {
    const container = this.lightbox.querySelector('.lightbox-hotspots');
    container.innerHTML = '';

    hotspots.forEach(hotspot => {
      const marker = document.createElement('button');
      marker.className = 'hotspot-marker';
      marker.style.left = `${hotspot.x}%`;
      marker.style.top = `${hotspot.y}%`;
      marker.innerHTML = '<span class="hotspot-pulse"></span><span class="hotspot-icon">+</span>';

      const tooltip = document.createElement('div');
      tooltip.className = 'hotspot-tooltip';
      tooltip.textContent = hotspot.text;
      marker.appendChild(tooltip);

      marker.addEventListener('mouseenter', () => {
        tooltip.style.display = 'block';
      });

      marker.addEventListener('mouseleave', () => {
        tooltip.style.display = 'none';
      });

      container.appendChild(marker);
    });
  }

  // ============ KEYBOARD NAVIGATION ============
  initKeyboardNav() {
    document.addEventListener('keydown', (e) => {
      if (!this.isLightboxOpen) return;

      switch(e.key) {
        case 'Escape':
          this.closeLightbox();
          break;
        case 'ArrowLeft':
          this.navigateLightbox(-1);
          break;
        case 'ArrowRight':
          this.navigateLightbox(1);
          break;
        case '+':
        case '=':
          this.adjustZoom(0.5);
          break;
        case '-':
          this.adjustZoom(-0.5);
          break;
        case '0':
          this.resetZoom();
          break;
      }
    });
  }

  // ============ PRELOADING ============
  preloadImages() {
    // Preload next and previous images
    const preloadIndexes = [
      (this.currentIndex + 1) % this.images.length,
      (this.currentIndex - 1 + this.images.length) % this.images.length
    ];

    preloadIndexes.forEach(index => {
      const img = new Image();
      img.src = this.images[index].src;
    });
  }

  // ============ COUNTER ============
  createCounter() {
    if (!this.options.enableCounter) return;

    const counter = document.createElement('div');
    counter.className = 'gallery-counter';
    counter.textContent = `1 / ${this.images.length}`;
    this.container.appendChild(counter);
    this.counterElement = counter;
  }

  updateCounter(index) {
    if (this.counterElement) {
      this.counterElement.textContent = `${index + 1} / ${this.images.length}`;
    }
  }

  // ============ TRANSITIONS ============
  attachEventListeners() {
    // Listen for gallery navigation
    const carousel = this.container.querySelector('scroll-carousel, .product-gallery__carousel');
    if (carousel) {
      carousel.addEventListener('scroll', () => {
        if (this.options.enableKenBurns) {
          this.applyKenBurns();
        }
      });
    }

    // Video autoplay on scroll
    if (this.options.autoplayDelay) {
      this.initVideoAutoplay();
    }
  }

  applyKenBurns() {
    const visibleImages = this.images.filter(img => {
      const rect = img.element.getBoundingClientRect();
      return rect.left >= 0 && rect.right <= window.innerWidth;
    });

    visibleImages.forEach(img => {
      const imageEl = img.element.querySelector('img');
      if (imageEl && !imageEl.classList.contains('ken-burns-active')) {
        imageEl.classList.add('ken-burns-active');
      }
    });
  }

  initVideoAutoplay() {
    const videos = this.container.querySelectorAll('video');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.play().catch(() => {});
          }, this.options.autoplayDelay);
        } else {
          entry.target.pause();
        }
      });
    }, { threshold: 0.5 });

    videos.forEach(video => observer.observe(video));
  }

  // ============ BEFORE/AFTER SLIDER ============
  createBeforeAfterSlider(element) {
    const container = element.closest('.product-gallery__media');
    const beforeImage = element.dataset.beforeImage;
    const afterImage = element.dataset.afterImage;

    if (!beforeImage || !afterImage) return;

    container.innerHTML = `
      <div class="before-after-container">
        <img src="${beforeImage}" alt="Before" class="before-image" />
        <img src="${afterImage}" alt="After" class="after-image" />
        <input type="range" min="0" max="100" value="50" class="before-after-slider" />
        <div class="slider-button">
          <svg width="20" height="20" viewBox="0 0 20 20">
            <polyline points="6 4 10 8 6 12" stroke="white" fill="none" stroke-width="2"/>
            <polyline points="14 4 10 8 14 12" stroke="white" fill="none" stroke-width="2"/>
          </svg>
        </div>
      </div>
    `;

    const slider = container.querySelector('.before-after-slider');
    const afterImg = container.querySelector('.after-image');
    const sliderButton = container.querySelector('.slider-button');

    slider.addEventListener('input', (e) => {
      const value = e.target.value;
      afterImg.style.clipPath = `inset(0 ${100 - value}% 0 0)`;
      sliderButton.style.left = `${value}%`;
    });
  }

  // ============ PUBLIC API ============
  destroy() {
    if (this.lightbox) {
      this.lightbox.remove();
    }
    if (this.counterElement) {
      this.counterElement.remove();
    }
  }

  goToSlide(index) {
    this.currentIndex = index;
    this.updateCounter(index);
  }
}

// Initialize on page load
if (typeof window !== 'undefined') {
  window.EnhancedProductGallery = EnhancedProductGallery;

  document.addEventListener('DOMContentLoaded', () => {
    const galleries = document.querySelectorAll('.product-gallery[data-enhanced="true"]');
    galleries.forEach(gallery => {
      const options = {
        enableZoom: gallery.dataset.enableZoom !== 'false',
        enableLightbox: gallery.dataset.enableLightbox !== 'false',
        enableTransitions: gallery.dataset.enableTransitions !== 'false',
        enableKenBurns: gallery.dataset.enableKenBurns === 'true',
        enableHotspots: gallery.dataset.enableHotspots === 'true',
        enableKeyboard: gallery.dataset.enableKeyboard !== 'false',
        enablePreload: gallery.dataset.enablePreload !== 'false',
        enableCounter: gallery.dataset.enableCounter === 'true',
        zoomLevel: parseInt(gallery.dataset.zoomLevel) || 3,
        autoplayDelay: parseInt(gallery.dataset.autoplayDelay) || 0,
      };

      new EnhancedProductGallery(gallery, options);
    });
  });
}
